import React, {useEffect, useState} from 'react';
import {Button, Card, Spin, Tag} from 'antd';
import {LockOutlined} from '@ant-design/icons';
import {useNavigate} from 'react-router-dom';
import {useI18n} from '../../i18n/useI18n';
import {useAuth} from '../../contexts/AuthContext';
import {getMyMembership, MembershipUserInfo} from '../../services/membershipService';
import MembershipCompareTable from './MembershipCompareTable';

const LEVEL_COLORS: Record<number, string> = {
    0: 'default',
    1: 'blue',
    2: 'purple',
    3: 'gold',
};

/**
 * 升级引导页（需求 5.2.6：无权限会员访问受限功能时显示）
 * 由 MembershipGate 在等级不足时内联渲染：
 * 等级对比表 + 定价 + 联系运营 CTA
 *
 * @author page
 * @date 2026-10-15
 */
interface Props {
    requiredLevel: number;
}

const MembershipUpgradePage: React.FC<Props> = ({requiredLevel}) => {
    const {t} = useI18n();
    const {user} = useAuth();
    const navigate = useNavigate();
    const [info, setInfo] = useState<MembershipUserInfo | null>(null);

    useEffect(() => {
        getMyMembership()
            .then(setInfo)
            .catch(() => setInfo(null));
    }, []);

    const currentLevel = user?.memberLevel ?? 0;
    const needText = t('membership.upgrade.need')
        .replace('{level}', t(`membership.level.${requiredLevel}`));

    return (
        <div style={{display: 'flex', justifyContent: 'center', padding: '48px 24px'}}>
            <div style={{maxWidth: 900, width: '100%', textAlign: 'center'}}>
                <LockOutlined style={{fontSize: 48, color: 'var(--pt-amber, #faad14)', marginBottom: 16}}/>
                <div style={{fontSize: 22, fontWeight: 600, marginBottom: 8}}>
                    {t('membership.upgrade.title')}
                </div>
                <div style={{color: 'var(--pt-text-secondary, #888)', marginBottom: 8}}>
                    {needText}
                </div>
                <div style={{color: 'var(--pt-text-secondary, #888)', marginBottom: 24}}>
                    {t('membership.upgrade.current')}：
                    <Tag color={LEVEL_COLORS[currentLevel] ?? 'default'}>
                        {t(`membership.level.${currentLevel}`)}
                    </Tag>
                </div>

                <Card style={{textAlign: 'left', marginBottom: 24}}>
                    {info ? (
                        <MembershipCompareTable pricing={info.pricing}/>
                    ) : (
                        <div style={{textAlign: 'center'}}><Spin/></div>
                    )}
                </Card>

                <div style={{color: 'var(--pt-text-secondary, #888)', marginBottom: 16}}>
                    {t('membership.upgrade.desc')}
                </div>
                <div style={{display: 'flex', gap: 12, justifyContent: 'center'}}>
                    <Button type="primary" onClick={() => window.open('/site/contact', '_blank')}>
                        {t('membership.center.contact')}
                    </Button>
                    <Button onClick={() => navigate('/')}>
                        {t('membership.upgrade.back')}
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default MembershipUpgradePage;
